import { LoadingOutlined } from '@ant-design/icons';
import { Button, Upload } from 'antd';
import { RcFile } from 'antd/lib/upload';
import { FC, useEffect, useState } from 'react';
import CloseIcon from '../../assets/images/shopping/closeIcon';
import DeleteFile from '../../assets/images/shopping/deleteFile';
import NodiIcon from '../../assets/images/shopping/nodiIcon';
import UploadIcon from '../../assets/images/shopping/uploadIcon';
import {
  IProductList,
  PAYMENT_TYPE_OPTIONS,
  PAYMENT_TYPE_OPTIONS_TEXT,
  PAYMENT_TYPES,
  TYPES_OF_BILLING
} from '../../types/orders';
import errorMessage from '../../utils/errorMessage';
import { OrderCardInListing } from '../../views/orders/Components';
import { NudosFileInput, NudosTextInput } from '../NudosComponents';
import { isFileValid } from '../../utils/validateFiles';
import NudosDatePicker from '../NudosComponents/NudosDatePicker';
import { isValidLink } from '../../utils/formValidations';
import { PAYMENT_METHODS_THAT_SHOW_LINK_AUTOMATED_PAYMENT_ORDERS } from '../../constants/constants';
import BordTooltip from '../BordTooltip/BordTooltip';
import { paymentDateBlockedMessage } from '../../utils/logisticServices.utils';

import './ModalConfirmPayment.scss';

interface IIConfirmPayment {
  updateData: IProductList;
  actionUpdate: (item: IProductList) => void;
  getGeneralData: () => void;
  loadingUpdate?: boolean;
}

type TOptionPayment = 'counted' | 'paymentAgreement' | 'care';

const ModalConfirmPayment: FC<IIConfirmPayment> = ({
  updateData,
  actionUpdate,
  getGeneralData,
  loadingUpdate
}: IIConfirmPayment) => {
  const [fileToUpload, setFileToUpload] = useState<File>();
  const [optionPayment, setOptionPayment] = useState<TOptionPayment>();
  const [loading, setLoading] = useState<boolean>(false);
  const [fileName, setFileName] = useState<string>();
  const [invoiceNumber, setInvoiceNumber] = useState<string>();
  const [documentInvoiceFile, setDocumentInvoiceFile] = useState<File>();
  const [temporalUrlInvoiceFile, setTemporalUrlInvoiceFile] = useState<string>();
  const [datePayment, setDatePayment] = useState<string>();
  const [paymentLink, setPaymentLink] = useState<string>(updateData?.paymentLink || '');
  const updateBillingProvider = updateData?.billingProvider || '';
  const linkError = paymentLink && !isValidLink(paymentLink) ? 'Link inválido' : '';
  const updateDataPaymentType: PAYMENT_TYPES = updateData.paymentType as PAYMENT_TYPES;
  const showLinkAndProofAutomatedPayment =
    updateDataPaymentType && PAYMENT_METHODS_THAT_SHOW_LINK_AUTOMATED_PAYMENT_ORDERS.includes(updateDataPaymentType);
  const verifyMethodTrasnfUsdAndBillingProvider =
    updateDataPaymentType === PAYMENT_TYPES.TRANSFER_USD &&
    (updateBillingProvider === TYPES_OF_BILLING.STRIPE_USA_NUDOS_LLC ||
      updateBillingProvider === TYPES_OF_BILLING.STRIPE_USA_NUDOS_CORP);

  const paymentMethodShouldNotShowPaymentLink = updateData?.paymentType?.includes('Transf');
  const withStripe = showLinkAndProofAutomatedPayment || verifyMethodTrasnfUsdAndBillingProvider;
  const disablePaymentAgreement = !updateDataPaymentType?.toLowerCase()?.includes('transf');
  const tooltipMessage = disablePaymentAgreement
    ? paymentDateBlockedMessage({ customClassName: 'alertTooltipPaymentAgreement' })
    : undefined;

  const countedPaymentType = optionPayment === PAYMENT_TYPE_OPTIONS.COUNTED;
  const paymentAgreementPaymentType = optionPayment === PAYMENT_TYPE_OPTIONS.PAYMENT_AGREEMENT;

  const handleUpdateData = () => {
    setLoading(true);
    try {
      if (actionUpdate && updateData) {
        const invoice = documentInvoiceFile;
        const proofOfPayment = fileToUpload;
        const data = {
          ...updateData,
          paymentLink,
          invoiceNumber,
          committedPaymentDate: datePayment,
          optionPayment,
          proofOfPayment,
          invoice
        };
        actionUpdate(data);
      }
    } catch (error) {
      errorMessage();
    } finally {
      setLoading(false);
    }
  };

  const verifyFileSheet = (file: File) => {
    const extension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    const isDocument = extension === '.docx' || extension === '.pdf';
    const size = file.size / 1024 / 1024 < 3;
    if (!isDocument) {
      errorMessage('solo puedes subir archivos .docx/.pdf');
      return true;
    } else if (!size) {
      errorMessage('el archivo debe pesar menos de 3MB');
    }
    return false;
  };

  const chargeFile = async (file: string | Blob | RcFile | File | any) => {
    const incorrectFile = verifyFileSheet(file);
    if (file && !incorrectFile) {
      setFileName(file?.name);
      setFileToUpload(file);
    }
  };

  const openVoucher = (url: string) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  const handleInvoiceFile = (invoiceFile: File) => {
    const isValid = isFileValid(
      invoiceFile,
      ['application/pdf', 'image/jpeg'],
      5,
      'El documento debe estar en formato PDF o JPG',
      'El documento no debe superar los 5MB'
    );
    if (isValid) {
      setDocumentInvoiceFile(invoiceFile);
      const base64File: string = URL.createObjectURL(invoiceFile);
      if (base64File) {
        setTemporalUrlInvoiceFile(base64File);
      }
    }
  };

  const openUrl = (url: string) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  const isCounted = optionPayment === 'counted';
  const isAgreement = optionPayment === 'paymentAgreement';
  const hasProofOfPaymentUrl = !!updateData?.proofOfPaymentUrl;
  const hasFile = !!fileToUpload;
  const hasPaymentLink = !!paymentLink;
  const hasDatePayment = !!datePayment;
  const isCountedValid = hasFile || hasProofOfPaymentUrl;
  const isDisabledBtn = () => {
    if (isCounted) {
      return !isCountedValid;
    }
    if (isAgreement) {
      return !hasDatePayment;
    }
    if (isAgreement && withStripe) {
      return !hasPaymentLink && !hasDatePayment;
    }
    return true;
  };
  const isDisabled = isDisabledBtn();
  const buttonIsDisabled = isDisabled || loading || loadingUpdate;

  const formatPaymentType = () => {
    if (countedPaymentType) return PAYMENT_TYPE_OPTIONS_TEXT.COUNTED;
    if (paymentAgreementPaymentType) return PAYMENT_TYPE_OPTIONS_TEXT.PAYMENT_AGREEMENT;
    return '';
  };

  useEffect(() => {
    if (optionPayment === 'counted') {
      setPaymentLink('');
      setDatePayment('');
    } else {
      setFileToUpload(undefined);
      setDocumentInvoiceFile(undefined);
    }
  }, [optionPayment]);

  useEffect(() => {}, [datePayment]);
  return (
    <div id="modalConfirmPayment">
      <div className="boxModal">
        <div className="containerModal">
          <div className="infoModal">
            <div
              className="floatClose"
              onClick={() => {
                if (getGeneralData) {
                  getGeneralData();
                }
              }}
            >
              <CloseIcon />
            </div>
            <div className="confirmPayment">
              <div className="containerIconNodi">
                <NodiIcon />
              </div>
              Pendiente de confirmación a Confirmado
            </div>
            <div className="subtitleModal text-fg-default">
              Estás a punto de actualizar el estado de la siguiente orden:
            </div>
            <div className="containerCard">
              <div className="boxCard">
                <OrderCardInListing data={updateData} withNotes={false} />
              </div>
            </div>
            <div className="limitsItemsModal">
              <div className="containerPaymentType">
                <div className="titlePaymentType">
                  Tipo de pago
                  <div className="separator">-</div>
                  <span className="detail">
                    {formatPaymentType()}
                    {updateData?.paymentType && ` (${updateData?.paymentType})`}
                  </span>
                </div>
                <div className="optionPaymentList">
                  <div className="selectOptionBox mr-2">
                    <div className="checkContainer">
                      {optionPayment === 'counted' ? (
                        <div className="checkOption">
                          <div className="checkOrange" />
                        </div>
                      ) : (
                        <div
                          className="noCheck"
                          onClick={() => {
                            setOptionPayment('counted');
                          }}
                        />
                      )}
                    </div>
                    <div className="textContainer">Al contado</div>
                  </div>
                  <div className="selectOptionBox mr-2">
                    <BordTooltip position="bottom" message={tooltipMessage}>
                      <div className="checkContainer">
                        {optionPayment === 'paymentAgreement' ? (
                          <div className="checkOption">
                            <div className="checkOrange" />
                          </div>
                        ) : (
                          <div
                            onClick={() => {
                              if (disablePaymentAgreement) return;
                              setOptionPayment('paymentAgreement');
                            }}
                            className={`noCheck ${disablePaymentAgreement ? 'disabledPaymentAgreementCheck' : ''}`}
                          />
                        )}
                      </div>

                      <div className={`textContainer ${disablePaymentAgreement ? 'disabledPaymentAgreementText' : ''}`}>
                        Acuerdo de pago
                      </div>
                    </BordTooltip>
                  </div>
                </div>
                {optionPayment === 'counted' && (
                  <div className="containerFile">
                    {fileToUpload || updateData?.proofOfPaymentUrl ? (
                      <div className="itemRedFile">
                        <div className="iconUpload" onClick={() => setFileToUpload(undefined)}>
                          <DeleteFile className="deleteFileIcon" />
                        </div>
                        <div className="textUploadDocumentGreen truncate">{fileName || 'Comprobante.pdf'}</div>
                      </div>
                    ) : (
                      <Upload
                        customRequest={e => {
                          if (e.file) chargeFile(e.file);
                        }}
                        showUploadList={false}
                      >
                        <div className="itemFile">
                          <div className="iconUpload">
                            <UploadIcon />
                          </div>
                          <div className="textUploadDocument">Comprobante</div>
                        </div>
                      </Upload>
                    )}
                    <div className="showVoucher">
                      {withStripe && updateData?.proofOfPaymentUrl ? (
                        <div
                          className="paymentNoticeTextContainer"
                          onClick={() => {
                            if (updateData?.proofOfPaymentUrl) openVoucher(updateData.proofOfPaymentUrl);
                          }}
                        >
                          Pago verificado por Stripe
                        </div>
                      ) : (
                        <div
                          className={`generalText ${updateData?.proofOfPaymentUrl ? 'voucherUploaded' : ''}`}
                          onClick={() => {
                            if (updateData?.proofOfPaymentUrl) openVoucher(updateData.proofOfPaymentUrl);
                          }}
                        >
                          Ver comprobante{' '}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {withStripe && optionPayment === 'counted' && (
                  <div className="paymentNoticeTextContainer">Pago pendiente de verificar por Stripe</div>
                )}

                {optionPayment === 'paymentAgreement' && (
                  <>
                    <div className="paymentAgreementContainer">
                      <div className="containerInputs">
                        {withStripe && !paymentMethodShouldNotShowPaymentLink && (
                          <div className="paymentLinkBox">
                            <NudosTextInput
                              label="Link de pago"
                              handleChange={e => setPaymentLink(e)}
                              isFilled={!!paymentLink}
                              errorText={linkError}
                              defaultValue={paymentLink || ''}
                              placeholder="Ingresa el link de stripe"
                            />
                          </div>
                        )}

                        <div className="datePickerBox">
                          <NudosDatePicker
                            label="Fecha de compromiso de pago"
                            componentSize="small"
                            isFilled={!!datePayment}
                            updateCurrentValueCallback={e => {
                              if (e) {
                                setDatePayment(e);
                              }
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div className="boxInvoiceSection">
                <div className="invoiceSection">
                  <div className="invoiceTitle">
                    <span className="font-bold">Factura</span> - (opcional)
                  </div>

                  <div className="numberInvoiceContainer">
                    <div className="invoiceBox">
                      <NudosTextInput
                        label="N° de Factura"
                        inputHeight={40}
                        isFilled={!!invoiceNumber}
                        handleChange={e => {
                          setInvoiceNumber(e);
                        }}
                        placeholder="Escribe el número de factura"
                      />
                    </div>
                  </div>
                  <div className="containerInvoiceBox">
                    <NudosFileInput
                      color="orange"
                      specialFill
                      isFilled={!!documentInvoiceFile || !!temporalUrlInvoiceFile}
                      customWidth="180px"
                      customHeight="40px"
                      buttonText="Factura"
                      uploadedFileHandler={e => {
                        if (e) {
                          handleInvoiceFile(e);
                        }
                      }}
                    />
                  </div>
                  <div className="previewInvoice">
                    <div className="previewContainer">
                      {temporalUrlInvoiceFile ? (
                        <div
                          className="paymentText paymentBlue"
                          onClick={() => {
                            openUrl(temporalUrlInvoiceFile);
                          }}
                        >
                          Ver factura{' '}
                        </div>
                      ) : (
                        <div className="paymentText">Ver factura</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="containerButton">
            <Button disabled={buttonIsDisabled} className="orangeButton" onClick={() => handleUpdateData()}>
              {loading || loadingUpdate ? <LoadingOutlined className="sizeLoader" /> : 'Guardar'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalConfirmPayment;
